import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Wand2, ExternalLink, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/ui";

export default function Inbox() {
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [accts, setAccts] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [acct, setAcct] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/emails");
    setEmails(res.data.emails);
    setCounts(res.data.counts);
    setAccts(res.data.accounts || []);
    setLoading(false);
    const wanted = new URLSearchParams(window.location.search).get("email");
    if (wanted) { const e = res.data.emails.find((x) => x.id === wanted); if (e) setSelected((s) => s || e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (e) => {
    setSelected(e); setDraft("");
    if (!e.read) { api.post(`/emails/${e.id}/read`).then(load); }
  };

  const extract = async (e) => {
    setBusy("extract");
    try {
      const res = await api.post(`/emails/${e.id}/extract-task`);
      if (res.data.created) toast.success(`Tâche créée : ${res.data.task.title}`);
      else toast.info("Aucune tâche à créer pour cet email");
      await load();
      setSelected({ ...e, analysis: res.data.analysis, category: res.data.analysis.category });
    } catch { toast.error("Analyse impossible"); } finally { setBusy(""); }
  };

  const makeDraft = async (e) => {
    setBusy("draft");
    try {
      const res = await api.post(`/emails/${e.id}/draft-reply`, {});
      setDraft(res.data.draft);
    } catch { toast.error("Brouillon impossible"); } finally { setBusy(""); }
  };

  const byCat = filter === "ALL" ? emails : emails.filter((e) => e.category === filter);
  const shown = acct === "ALL" ? byCat
    : acct.startsWith("id:") ? byCat.filter((e) => e.source_account_id === acct.slice(3))
    : byCat.filter((e) => e.account_type === acct);
  const hasMulti = accts.length > 1;

  const TYPE_LABEL = { WORK: "Pro", WORK_LEGACY: "Pro (ancien)", PERSONAL: "Perso", OTHER: "Autre" };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Inbox</h1>
        <p className="text-muted-foreground mt-1 text-sm">Chaque email trié automatiquement par l'IA en catégories d'action.</p>
      </div>

      {hasMulti && (
        <div className="flex flex-wrap gap-2" data-testid="inbox-account-filters">
          <FilterChip active={acct === "ALL"} onClick={() => setAcct("ALL")} label="Tous les comptes" count={emails.length} testid="inbox-acct-all" />
          {["WORK", "WORK_LEGACY", "PERSONAL"].map((t) => {
            const n = emails.filter((e) => e.account_type === t).length;
            return n ? <FilterChip key={t} active={acct === t} onClick={() => setAcct(t)} label={TYPE_LABEL[t]} count={n} testid={`inbox-acct-${t}`} /> : null;
          })}
          {accts.map((a) => (
            <FilterChip key={a.id} active={acct === `id:${a.id}`} onClick={() => setAcct(`id:${a.id}`)}
              label={a.email_address} count={a.count} testid={`inbox-acct-addr-${a.email_address}`} />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="inbox-filters">
        <FilterChip active={filter === "ALL"} onClick={() => setFilter("ALL")} label="Tout" count={emails.length} />
        {CATEGORY_ORDER.map((c) => counts[c] ? (
          <FilterChip key={c} active={filter === c} onClick={() => setFilter(c)}
            label={CATEGORY_META[c].label} count={counts[c]} cls={CATEGORY_META[c].cls} testid={`inbox-filter-${c}`} />
        ) : null)}
      </div>

      {loading ? (
        <div className="h-[40vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-2.5" data-testid="inbox-list">
            {shown.map((e) => {
              const m = CATEGORY_META[e.category] || CATEGORY_META.FYI;
              return (
                <motion.button key={e.id} whileHover={{ x: 2 }} onClick={() => open(e)}
                  data-testid={`inbox-email-${e.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${selected?.id === e.id ? "border-primary/50 bg-primary/5" : "border-border hover:border-border/80 bg-card"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {!e.read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                      <span className="text-sm font-medium truncate">{e.from_name}</span>
                    </div>
                    <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border shrink-0 ${m.cls}`}>{m.label}</span>
                  </div>
                  <div className="text-sm mt-1 truncate">{e.subject}</div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="text-xs text-muted-foreground line-clamp-1">{e.snippet}</div>
                    {hasMulti && e.account_type && (
                      <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0" title={e.account_email}>
                        {TYPE_LABEL[e.account_type] || e.account_type}
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
            {shown.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center" data-testid="inbox-empty">
              {emails.length === 0 ? "Aucun email. Connecte un compte Gmail dans Réglages puis lance une synchronisation." : "Aucun email dans cette catégorie."}
            </div>}
          </div>

          {/* Detail */}
          <div className="lg:sticky lg:top-4 h-fit">
            {selected ? (
              <motion.div key={selected.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                data-testid="inbox-detail" className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-muted-foreground">{selected.from_name}</div>
                    <h2 className="font-display text-xl font-semibold mt-0.5">{selected.subject}</h2>
                  </div>
                  {selected.project && selected.project !== "Autre" && (
                    <span className="text-xs text-muted-foreground border border-border rounded-full px-2.5 py-1">{selected.project}</span>
                  )}
                </div>

                {selected.analysis?.summary && (
                  <div className="mt-4 rounded-xl bg-primary/5 border border-primary/20 p-3.5">
                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                      <Sparkles className="w-3 h-3" /> Résumé IA
                    </div>
                    <div className="text-sm">{selected.analysis.summary}</div>
                  </div>
                )}

                <div className="mt-4 text-sm text-muted-foreground whitespace-pre-line leading-relaxed max-h-52 overflow-y-auto airtime-scroll">
                  {selected.body}
                </div>
                {selected.account_email && (
                  <div className="mt-3 text-xs text-muted-foreground">Compte : <span className="text-foreground">{selected.account_email}</span></div>
                )}

                <div className="mt-5 flex flex-wrap gap-2.5">
                  <button data-testid="inbox-extract-task" onClick={() => extract(selected)} disabled={busy === "extract"}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground font-medium hover:opacity-90 transition-all disabled:opacity-60">
                    {busy === "extract" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Extraire la tâche
                  </button>
                  <button data-testid="inbox-draft-reply" onClick={() => makeDraft(selected)} disabled={busy === "draft"}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-all disabled:opacity-60">
                    {busy === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Brouillon de réponse
                  </button>
                  <a data-testid="inbox-open-mail" href={`mailto:${selected.from}?subject=RE: ${encodeURIComponent(selected.subject)}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-all">
                    <ExternalLink className="w-4 h-4" /> Ouvrir dans Mail
                  </a>
                </div>

                {draft && (
                  <div className="mt-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-400" /> Brouillon proposé — à valider avant envoi
                    </div>
                    <textarea data-testid="inbox-draft-text" value={draft} onChange={(e) => setDraft(e.target.value)}
                      className="w-full h-44 rounded-xl bg-secondary border border-border p-3.5 text-sm resize-none airtime-scroll" />
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                Sélectionne un email pour voir l'analyse IA et agir.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count, cls = "", testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium border transition-all
        ${active ? "border-primary/50 bg-primary/10 text-primary" : `border-border text-muted-foreground hover:text-foreground ${cls}`}`}>
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}
